'use client';
import { useState, useEffect, useRef } from 'react';
import ChimeKitIcon from 'components/shared/LinkWithIcon/ChimeKitIcon';

interface LinkToCopyProps {
  linkToCopy: string;
}

const CopyToClipboard = ({ linkToCopy }: LinkToCopyProps) => {
  const [hasClipboard, setHasClipboard] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const isSupported = typeof window !== 'undefined' && !!navigator?.clipboard?.writeText;

    if (isSupported) {
      queueMicrotask(() => setHasClipboard(true));
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const copyLink = () => {
    navigator.clipboard
      .writeText(linkToCopy)
      .then(() => {
        setIsCopied(true);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setIsCopied(false), 3000);
      })
      .catch(() => setIsCopied(false));
  };

  if (!hasClipboard) return null;

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={copyLink}
        data-testid="clipboard-button"
        className="flex justify-center items-center w-8 h-8 dark-green-colorway hover:medium-green-colorway rounded-full transition duration-300">
        <span className="sr-only">Copy URL to clipboard.</span>
        <ChimeKitIcon
          iconName="Hyperlink"
          className="block w-auto h-4 fill-white hover:fill-primary transition duration-300"
        />
      </button>
      {isCopied && <p className="font-sans font-semibold text-xs text-content-primary">Copied!</p>}
    </div>
  );
};

export default CopyToClipboard;
